import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import css from './pages.module.css';
import {useAuth} from '../context/AuthProvider';
import {useTheme} from '../context/ThemeProvider';
import {useToast} from '../context/ToastProvider';
import {useConfirm} from '../context/ConfirmProvider';
import {useUpgrade} from '../context/UpgradeProvider';
import {useNotes} from '../context/NotesProvider';
import {
  isAppleLinked,
  isGoogleLinked,
  getAppleEmail,
  getGoogleEmail,
  signOutForAccountSwitch,
} from '../services/authService';
import {linkOrMerge, type LinkProvider} from '../services/accountLink';
import {deleteAccount} from '../services/functionsService';
import {
  setAppLanguage,
  getVoiceLanguage,
  setVoiceLanguage,
  getSummaryLanguage,
  setSummaryLanguage,
  type AppearancePreference,
} from '../services/settings';
import {SUPPORTED_APP_LANGUAGES} from '../i18n';
import {releaseBusyOnRefocus} from '../utils';
import {LANGUAGES, flagEmoji} from '../services/languages';
import {Button} from '../components/common/ui';
import {Icon} from '../components/common/Icon';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  PRIVACY_URL,
  TERMS_URL,
  FEEDBACK_EMAIL,
} from '../constants';

const APP_LANGUAGE_NAMES: Record<string, string> = {
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  ja: '日本語',
  pt: 'Português',
  ru: 'Русский',
  tr: 'Türkçe',
};

export function SettingsPage() {
  const {t, i18n} = useTranslation();
  const {user, entitlement, refreshUser} = useAuth();
  const {notes} = useNotes();
  const {preference, setPreference} = useTheme();
  const showToast = useToast();
  const confirm = useConfirm();
  const {showUpgrade} = useUpgrade();

  const [linking, setLinking] = useState<LinkProvider | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [voiceLang, setVoiceLangState] = useState(getVoiceLanguage());
  const [summaryLang, setSummaryLangState] = useState(getSummaryLanguage());

  const handleLink = async (provider: LinkProvider) => {
    setLinking(provider);
    // Unfreeze the buttons if the OAuth popup is closed without finishing
    // (the SDK's popup-closed rejection can lag or never fire).
    const cleanup = releaseBusyOnRefocus(() => setLinking(null));
    try {
      const result = await linkOrMerge(provider, () =>
        confirm({
          title: t('MergeConfirmTitle'),
          message: t('MergeConfirmText', {count: notes.length}),
          confirmText: t('Merge'),
        }),
      );
      if (result.status === 'linked') {
        showToast(t('AccountLinked'), 'success');
      } else if (result.status === 'merged') {
        showToast(t('MergeDoneText', {count: result.mergedNotes ?? 0}), 'success');
      } else if (result.status === 'error') {
        showToast(t('Error'), 'error');
      }
    } finally {
      cleanup();
      setLinking(null);
      refreshUser();
    }
  };

  // Sign-out drops the session; AppShell falls back to the login screen.
  const handleSignOut = async () => {
    await signOutForAccountSwitch();
    refreshUser();
  };

  const handleDeleteAccount = async () => {
    if (deleting) {
      return;
    }
    const ok = await confirm({
      title: t('DeleteAccount'),
      message: t('DeleteAccountConfirm'),
      confirmText: t('Delete'),
      danger: true,
    });
    if (!ok) {
      return;
    }
    setDeleting(true);
    try {
      await deleteAccount();
      await signOutForAccountSwitch();
      refreshUser();
      showToast(t('Done'), 'success');
    } catch (e) {
      console.warn(e);
      showToast(t('Error'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const languageSelect = (
    value: string | null,
    onChange: (code: string | null) => void,
  ) => (
    <select
      className={css.select}
      value={value ?? ''}
      onChange={e => onChange(e.target.value || null)}
    >
      {LANGUAGES.map(lang => (
        <option key={`${lang.code}-${lang.flag}`} value={lang.code ?? ''}>
          {lang.code ? `${flagEmoji(lang.flag)} ${lang.language}` : t('Automatic')}
        </option>
      ))}
    </select>
  );

  return (
    <div className={css.page}>
      <header className={css.pageHeader}>
        <h1 className={css.pageTitle}>{t('Settings')}</h1>
      </header>
      <div className={css.scroll}>
        <div className={css.settingsWrap}>
          {/* Account */}
          <div className={css.sectionTitle}>{t('Account')}</div>
          <div className={css.section}>
            {user && isGoogleLinked(user) && (
              <div className={css.row}>
                <Icon name="google" size={18} />
                <div className={css.rowLabel}>
                  {t('GoogleLinkedLabel')}
                  <div className={css.rowSub}>{getGoogleEmail(user)}</div>
                </div>
              </div>
            )}
            {user && isAppleLinked(user) && (
              <div className={css.row}>
                <Icon name="apple" size={18} />
                <div className={css.rowLabel}>
                  {t('AppleLinkedLabel')}
                  <div className={css.rowSub}>{getAppleEmail(user)}</div>
                </div>
              </div>
            )}
            <div className={css.row} style={{gap: 10, flexWrap: 'wrap'}}>
              {user && !isGoogleLinked(user) && (
                <Button
                  variant="secondary"
                  icon="google"
                  loading={linking === 'google'}
                  onClick={() => handleLink('google')}
                >
                  {t('ContinueWithGoogle')}
                </Button>
              )}
              {user && !isAppleLinked(user) && (
                <Button
                  variant="secondary"
                  icon="apple"
                  loading={linking === 'apple'}
                  onClick={() => handleLink('apple')}
                >
                  {t('ContinueWithApple')}
                </Button>
              )}
              <Button variant="ghost" icon="logout" onClick={handleSignOut}>
                {t('WebSignOut')}
              </Button>
            </div>
            <div className={css.row}>
              <div className={css.rowLabel} style={{color: 'var(--color-danger)'}}>
                {t('DeleteAccount')}
              </div>
              <Button variant="danger" onClick={handleDeleteAccount} loading={deleting}>
                {t('Delete')}
              </Button>
            </div>
          </div>

          {/* Premium */}
          <div className={css.sectionTitle}>{t('Premium')}</div>
          <div className={css.section}>
            <div className={css.row}>
              <div className={css.rowLabel}>
                {entitlement === 'pro' ? 'Owlly Pro ✓' : t('upgrade_premium')}
                {entitlement !== 'pro' && (
                  <div className={css.rowSub}>{t('WebSameAccountHint')}</div>
                )}
              </div>
              {entitlement !== 'pro' && (
                <Button
                  variant="primary"
                  icon="crown"
                  onClick={() => showUpgrade('generic')}
                >
                  {t('WebGetTheApp')}
                </Button>
              )}
            </div>
          </div>

          {/* Preferences */}
          <div className={css.sectionTitle}>{t('Preferences')}</div>
          <div className={css.section}>
            <div className={css.row}>
              <div className={css.rowLabel}>{t('Appearance')}</div>
              <div className={css.segmented}>
                {(['system', 'light', 'dark'] as AppearancePreference[]).map(
                  option => (
                    <button
                      key={option}
                      className={
                        preference === option ? css.segmentActive : css.segment
                      }
                      onClick={() => setPreference(option)}
                      type="button"
                    >
                      {t(
                        option === 'system'
                          ? 'AppearanceSystem'
                          : option === 'light'
                            ? 'AppearanceLight'
                            : 'AppearanceDark',
                      )}
                    </button>
                  ),
                )}
              </div>
            </div>
            <div className={css.row}>
              <div className={css.rowLabel}>{t('AppLanguage')}</div>
              <select
                className={css.select}
                value={i18n.language}
                onChange={e => {
                  setAppLanguage(e.target.value);
                  void i18n.changeLanguage(e.target.value);
                }}
              >
                {SUPPORTED_APP_LANGUAGES.map(code => (
                  <option key={code} value={code}>
                    {APP_LANGUAGE_NAMES[code] ?? code}
                  </option>
                ))}
              </select>
            </div>
            <div className={css.row}>
              <div className={css.rowLabel}>{t('VoiceLanguage')}</div>
              {languageSelect(voiceLang, code => {
                setVoiceLanguage(code);
                setVoiceLangState(code);
              })}
            </div>
            <div className={css.row}>
              <div className={css.rowLabel}>{t('SummaryLanguage')}</div>
              {languageSelect(summaryLang, code => {
                setSummaryLanguage(code);
                setSummaryLangState(code);
              })}
            </div>
          </div>

          {/* Support & legal */}
          <div className={css.sectionTitle}>{t('SupportFeadback')}</div>
          <div className={css.section}>
            <div className={css.row}>
              <div className={css.rowLabel}>{t('SendFeedback')}</div>
              <a href={`mailto:${FEEDBACK_EMAIL}`}>
                <Button variant="ghost" icon="externalLink">
                  {FEEDBACK_EMAIL}
                </Button>
              </a>
            </div>
            <div className={css.row}>
              <div className={css.rowLabel}>{t('WebGetTheApp')}</div>
              <div style={{display: 'flex', gap: 8}}>
                <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
                  <Button variant="ghost" icon="apple">
                    iOS
                  </Button>
                </a>
                <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
                  <Button variant="ghost" icon="externalLink">
                    Android
                  </Button>
                </a>
              </div>
            </div>
            <div className={css.row}>
              <div className={css.rowLabel}>{t('Legal')}</div>
              <div style={{display: 'flex', gap: 14}}>
                <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
                  {t('PrivacyPolicy')}
                </a>
                <a href={TERMS_URL} target="_blank" rel="noreferrer">
                  {t('TermsConditions')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
